export const CRYPTO_DECLARE_CURRENCIES = ['BTC', 'ETH', 'USDT'] as const;

const NETWORK_GROUPS: Record<string, string[]> = {
  tron: ['tron', 'trc20', 'trx'],
  ethereum: ['ethereum', 'erc20', 'eth'],
  polygon: ['polygon', 'matic', 'poly'],
  bitcoin: ['bitcoin', 'btc'],
};

export function isCryptoDeclareCurrency(currency: unknown): boolean {
  return CRYPTO_DECLARE_CURRENCIES.includes(
    String(currency ?? '')
      .trim()
      .toUpperCase() as (typeof CRYPTO_DECLARE_CURRENCIES)[number],
  );
}

function asRecord(value: unknown): any {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : null;
}

function collectNetworkHints(parts: unknown[]): string[] {
  const text = parts
    .map((value) => String(value ?? '').trim().toUpperCase())
    .filter(Boolean)
    .join(' ');
  const hints = new Set<string>();
  for (const [group, aliases] of Object.entries(NETWORK_GROUPS)) {
    if (aliases.some((alias) => text.includes(alias.toUpperCase()))) {
      hints.add(group);
      aliases.forEach((alias) => hints.add(alias));
    }
  }
  return [...hints];
}

export function extractDepositAddress(source: unknown, depth = 0): string {
  if (!source || depth > 4) {
    return '';
  }

  if (Array.isArray(source)) {
    for (const item of source) {
      const found = extractDepositAddress(item, depth + 1);
      if (found) {
        return found;
      }
    }
    return '';
  }

  const obj = asRecord(source);
  if (!obj) {
    return '';
  }

  const metadata = asRecord(obj.metadata) || {};
  const candidates = [
    obj.address,
    obj.wallet_address,
    obj.deposit_address,
    metadata.address,
    metadata.wallet_address,
    metadata.deposit_address,
  ];
  for (const candidate of candidates) {
    const value = String(candidate ?? '').trim();
    if (value) {
      return value;
    }
  }

  return (
    extractDepositAddress(obj.tokens, depth + 1) ||
    extractDepositAddress(obj.wallets, depth + 1) ||
    extractDepositAddress(metadata.tokens, depth + 1) ||
    extractDepositAddress(metadata.wallets, depth + 1) ||
    (Array.isArray(obj.metadata)
      ? extractDepositAddress(obj.metadata, depth + 1)
      : '')
  );
}

export function walletNetworkHints(wallet: any): string[] {
  return collectNetworkHints([
    wallet?.id,
    wallet?.shortname,
    wallet?.name,
    wallet?.metadata?.blockchain,
    wallet?.metadata?.network,
  ]);
}

export function providerNetworkHints(provider: any): string[] {
  return collectNetworkHints([
    provider?.id,
    provider?.metadata?.blockchain,
    provider?.metadata?.network,
    provider?.metadata?.wallet,
    provider?.provider?.username,
    provider?.provider?.shortname,
    provider?.username,
    provider?.shortname,
  ]);
}

export function providerMatchesWallet(provider: any, wallet: any): boolean {
  const walletId = String(wallet?.id ?? '')
    .trim()
    .toUpperCase();
  const metadata = asRecord(provider?.metadata) || {};
  const linkedIds = [
    metadata.wallet,
    metadata.wallet_id,
    metadata.destiny_wallet_id,
  ]
    .map((value) => String(value ?? '').trim().toUpperCase())
    .filter(Boolean);

  if (walletId && linkedIds.includes(walletId)) {
    return true;
  }

  const walletHints = walletNetworkHints(wallet);
  const providerHints = providerNetworkHints(provider);
  return walletHints.some((hint) => providerHints.includes(hint));
}

export function findMatchingDepositProvider(
  providers: any[] | null | undefined,
  wallet: any,
): any | null {
  const list = providers ?? [];
  return (
    list.find(
      (provider) =>
        providerMatchesWallet(provider, wallet) &&
        extractDepositAddress(provider),
    ) ||
    list.find((provider) => providerMatchesWallet(provider, wallet)) ||
    null
  );
}

export function flattenBitgoAddresses(
  payload: any,
): Array<{ address: string; network?: string }> {
  const out: Array<{ address: string; network?: string }> = [];
  const push = (item: any, fallbackNetwork?: string) => {
    const address = extractDepositAddress(item);
    if (!address) {
      return;
    }
    const network = String(item?.network ?? fallbackNetwork ?? '')
      .trim()
      .toLowerCase();
    out.push(network ? { address, network } : { address });
  };

  const data = payload?.data ?? payload;
  if (Array.isArray(data?.roots)) {
    for (const root of data.roots) {
      push(root, root?.network);
      for (const address of root?.addresses || []) {
        push(address, address?.network || root?.network);
      }
    }
  }
  for (const wallet of data?.wallets || []) {
    push(wallet);
  }
  for (const wallet of data?.legacy_or_orphan_addresses || []) {
    push(wallet);
  }
  if (Array.isArray(data)) {
    for (const wallet of data) {
      push(wallet);
    }
  }
  return out;
}

export function findBitgoAddressForWallet(
  addresses: Array<{ address: string; network?: string }> | null | undefined,
  wallet: any,
): string {
  const hints = walletNetworkHints(wallet);
  const match = (addresses ?? []).find((item) => {
    const network = String(item.network ?? '').toLowerCase();
    return Boolean(network && hints.includes(network));
  });
  return match?.address || '';
}

export function resolveDeclaredCryptoDeposit(input: {
  wallet: any;
  providers?: any[];
  extraSources?: any[];
  bitgoAddresses?: Array<{ address: string; network?: string }>;
}): { address: string; network: string; provider: any | null } {
  const wallet = input.wallet;
  const provider = findMatchingDepositProvider(input.providers, wallet);
  const extraAddress = (input.extraSources ?? [])
    .map((source) => extractDepositAddress(source))
    .find(Boolean);
  const address =
    extractDepositAddress(wallet) ||
    extraAddress ||
    extractDepositAddress(provider) ||
    findBitgoAddressForWallet(input.bitgoAddresses, wallet) ||
    '';
  const network = String(
    provider?.metadata?.blockchain ??
      provider?.metadata?.network ??
      wallet?.metadata?.blockchain ??
      wallet?.shortname ??
      wallet?.name ??
      wallet?.id ??
      '',
  ).trim();

  return { address, network, provider };
}

export function buildCryptoDeclarationMetadata(input: {
  reference: string;
  wallet?: any;
  address?: string;
  network?: string;
}): Record<string, unknown> {
  const metadata: Record<string, unknown> = {
    reference: input.reference,
  };
  if (input.network) {
    metadata['network'] = input.network;
  }
  if (input.address) {
    metadata['deposit_address'] = input.address;
  }
  if (input.wallet?.id) {
    metadata['wallet_id'] = input.wallet.id;
  }
  return metadata;
}

export function parseProviderServiceId(provider: any): number | null {
  const raw = provider?.provider_service_id ?? provider?.id;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}
